import { EditorContent, useEditor } from '@tiptap/react';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { TextSelection } from '@tiptap/pm/state';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';

import { fieldIdentity } from '@dealstack/shared/lib/documents/scopedFieldNames';

import { FIELD_NODE_NAME } from './extensions/fieldNode';
import { FieldSuggestion, filterFieldItems } from './extensions/fieldSuggestion';
import type { FieldSuggestionItem } from './extensions/fieldSuggestion';
import { handleFieldDrop } from './extensions/fieldDrop';
import { handleSignatureDrop, insertSignatureAt } from './extensions/signatureDrop';
import {
	caretAfterSignatureInsert,
	findSignatureForSlot,
	placedSignatureKeys,
	removeSignatureSlot,
} from './extensions/signatureCommands';
import {
	findDates,
	handleDateDrop,
	insertDateAt,
	placedDateIds,
	removeDateAnchor,
} from './extensions/dateCommands';
import { flowExtensions } from './extensions/flowExtensions';
import { createFieldSuggestionRenderer } from './fieldSuggestionRenderer';
import { fromEditorContent, toEditorContent } from './flowDocument';
import { clearRules, setSelectionRule } from './ruleCommands';
import type { EditorJsonNode } from './flowDocument';
import { EditorToolbar } from './EditorToolbar';
import { EditorScroll, EditorSurface, EditorStack } from './flowDocumentEditor.styled';
import { fontStackFor } from './fontStack';
import type { FlowDocumentEditorHandle, FlowDocumentEditorProps } from './flowDocumentEditor.types';

/**
 * An editable view of a flow document.
 *
 * `document` is read ONCE, on mount. Feeding it back in on every change would reset the
 * selection mid-typing, so the parent holds the latest value from `onChange` and does not push
 * it back — remount with a `key` to load a different document.
 */
export const FlowDocumentEditor = forwardRef<FlowDocumentEditorHandle, FlowDocumentEditorProps>(
	(
		{
			document: flowDocument,
			fields,
			knownFieldLabels,
			signatureSlots,
			showStampMetadata = true,
			ruleLabelFor,
			ruleActions,
			onChange,
			onUnrepresentableContent,
			editable = true,
		},
		ref
	) => {
		/**
		 * `fields` is read through a ref, not closed over.
		 *
		 * The suggestion plugin is built once, when the editor is constructed: TipTap's
		 * `setOptions` merges options but never re-runs `createExtensionManager`, so a callback
		 * closing over `fields` keeps whatever had loaded by mount. The data-point catalogue is
		 * a second request and routinely arrives later — guaranteed on a revisit, where the
		 * document query serves from cache instantly — which left the `{{` picker permanently
		 * showing "No data point matches" while the palette beside it listed everything.
		 */
		const fieldsRef = useRef(fields);
		fieldsRef.current = fields;

		const items = useCallback(
			(query: string): FieldSuggestionItem[] => filterFieldItems(fieldsRef.current, query),
			[]
		);

		// Read through the same ref, and for the same reason: the node's options are bound when
		// the editor is constructed. A chip already on the page keeps whatever label was known
		// then, which is why the caller waits for the catalogue before mounting the editor.
		const knownLabelsRef = useRef(knownFieldLabels);
		knownLabelsRef.current = knownFieldLabels;

		// Labels by IDENTITY, for a name whose cosmetic slug has since changed: renaming a
		// security or scheme changes the minted name, but not the field already in the document.
		const byIdentity = useMemo(() => {
			const labels = new Map<string, string>();
			const record = (name: string, label: string): void => {
				const identity = fieldIdentity(name);
				if (identity !== null && !labels.has(identity)) {
					labels.set(identity, label);
				}
			};
			fields.forEach((field) => record(field.name, field.label));
			Object.entries(knownFieldLabels ?? {}).forEach(([name, label]) => record(name, label));
			return labels;
		}, [fields, knownFieldLabels]);
		const byIdentityRef = useRef(byIdentity);
		byIdentityRef.current = byIdentity;

		// Palette first, then the caller's lookup (a scoped field whose security is not pinned),
		// then identity (renamed since). Humanising is the last resort and reads as a key.
		const labelFor = useCallback((name: string): string | undefined => {
			const offered = fieldsRef.current.find((field) => field.name === name)?.label;
			if (offered !== undefined) {
				return offered;
			}
			const known = knownLabelsRef.current?.[name];
			if (known !== undefined) {
				return known;
			}
			const identity = fieldIdentity(name);
			return identity === null ? undefined : byIdentityRef.current.get(identity);
		}, []);

		/**
		 * The bindings the document arrived with, read once for the same reason it is.
		 *
		 * A save MERGES bindings rather than replacing them, so one already stored survives even
		 * when the catalogue no longer offers that name — a data point renamed since, say. Kept
		 * so such a field is not called unbound when a value will in fact reach the deed.
		 */
		const loadedBindingsRef = useRef(flowDocument.fieldBindings ?? {});

		/**
		 * Whether a value will be rendered for `name`.
		 *
		 * True when the catalogue offers the name — the save sends a binding for every offered
		 * name the document holds — or when the document already carries one. False is the
		 * honest answer for a name nobody can bind: the palette only mints catalogue names, so
		 * a hand-typed name that is not one of them has no route to a binding and generation
		 * has nothing to put there.
		 */
		const isBound = useCallback(
			(name: string): boolean =>
				fieldsRef.current.some((field) => field.name === name) ||
				loadedBindingsRef.current[name] !== undefined ||
				// Labelled but not currently offered — a scoped field whose security is not
				// pinned. Still bindable, and without this a correctly-bound chip showed as
				// will-render-blank the moment the pin moved.
				labelFor(name) !== undefined,
			[labelFor]
		);

		/**
		 * The document as it was when the editor opened, for comparison.
		 *
		 * A ProseMirror node, compared with `Node.eq`: structural, early-exiting, and the
		 * editor's own notion of sameness. Taken from the editor rather than from `document`
		 * so anything the schema normalises on load — a token healed into a field node — is not
		 * counted as an edit the author made.
		 */
		const baseline = useRef<ProseMirrorNode | null>(null);

		// Read through a ref for the same reason `labelFor` is: the node's options are bound
		// when the editor is constructed, so an anchor already on the page would keep whatever
		// slots had loaded by mount — and the slot list is a second request.
		const signatureSlotsRef = useRef(signatureSlots ?? {});
		signatureSlotsRef.current = signatureSlots ?? {};

		const displayFor = useCallback((slotKey: string) => signatureSlotsRef.current[slotKey], []);

		// Same arrangement as `labelFor`: the extension's options are bound when the editor is
		// constructed, so the lookup has to be read through a ref or a rule renamed after mount
		// would keep the name it had then.
		const ruleLabelRef = useRef(ruleLabelFor);
		ruleLabelRef.current = ruleLabelFor;
		const ruleLabel = useCallback((ruleId: string) => ruleLabelRef.current?.(ruleId), []);

		const editor = useEditor({
			editable,
			extensions: [
				...flowExtensions({
					labelFor,
					isBound,
					signature: { displayFor, showStampMetadata },
					rule: { labelFor: ruleLabel },
					date: { showStampMetadata },
				}),
				FieldSuggestion.configure({ items, render: createFieldSuggestionRenderer() }),
			],
			content: toEditorContent(flowDocument),
			editorProps: {
				// Each keys on its own MIME type, so the order never decides which claims a drop.
				// Only palette drags arrive here; placed anchors move by a pointer gesture.
				handleDrop: (view, event) =>
					event instanceof DragEvent
						? handleFieldDrop(view, event) ||
							handleSignatureDrop(view, event) ||
							handleDateDrop(view, event)
						: false,
				attributes: { 'data-testid': 'flow-document-editor' },
			},
			onCreate: ({ editor: instance }) => {
				baseline.current = instance.state.doc;
			},
			onUpdate: ({ editor: instance }) => {
				if (onChange === undefined) {
					return;
				}
				if (instance.state.doc.eq(baseline.current ?? instance.state.doc)) {
					onChange(null);
					onUnrepresentableContent?.(false);
					return;
				}
				try {
					// Validated on the way out, so a malformed document is caught here rather
					// than at render time, when it is no longer clear which edit produced it.
					onChange(fromEditorContent(instance.getJSON() as EditorJsonNode, flowDocument));
					onUnrepresentableContent?.(false);
				} catch {
					// A paste can put a node in the editor that the flow model cannot hold — an
					// image with no stated size, say. Letting this throw stopped `onUpdate` for
					// the life of the editor, so the author kept typing into a document nothing
					// was reading and the save wrote what was there before the paste. Said out
					// loud instead, and the caller stops the save.
					onUnrepresentableContent?.(true);
				}
			},
		});

		useImperativeHandle(ref, () => ({
			setSelectionRule: (ruleId: string | null) =>
				editor === null ? 0 : setSelectionRule(editor, ruleId),
			clearRules: (ruleIds: ReadonlySet<string>) => {
				if (editor !== null) {
					clearRules(editor, ruleIds);
				}
			},
			// Labels are read from a ref at draw time, so a rename changes nothing in the
			// document and ProseMirror has no reason to redraw. An empty transaction is the
			// cheapest way to ask it to.
			refreshRules: () => {
				if (editor !== null) {
					editor.view.dispatch(editor.state.tr);
				}
			},
			markSaved: () => {
				if (editor !== null) {
					baseline.current = editor.state.doc;
				}
			},
			insertField: (field: FieldSuggestionItem) => {
				editor
					?.chain()
					.focus()
					.insertContent([
						{
							type: FIELD_NODE_NAME,
							attrs: { name: field.name, filter: field.filter ?? null },
						},
						// A trailing space, so the caret lands in text rather than against the
						// atom and the next keystroke is not swallowed by it.
						{ type: 'text', text: ' ' },
					])
					.run();
			},
			insertSignature: (slot) => {
				if (editor === null) {
					return false;
				}
				// Routed through the same `insertPoint`-based helper the drop uses, so a caret
				// inside a paragraph puts the anchor AFTER that paragraph rather than being
				// refused — and a caret somewhere no block is allowed is refused, not thrown.
				const transaction = insertSignatureAt(
					editor.state,
					editor.state.selection.from,
					slot
				);
				if (transaction === null) {
					return false;
				}
				// Advance into the next cell of a two-column pair, so the second Place lands
				// BESIDE the first instead of under it. `TextSelection.near` rather than an
				// exact position: the cell's first child is whatever the author left there.
				const caret = caretAfterSignatureInsert(
					transaction.doc,
					transaction.mapping.map(editor.state.selection.from)
				);
				if (caret !== null) {
					transaction.setSelection(TextSelection.near(transaction.doc.resolve(caret), 1));
				}
				editor.view.dispatch(transaction);
				editor.commands.focus();
				return true;
			},
			insertSignatureBlock: () => {
				if (editor === null) {
					return false;
				}
				// `withHeaderRow: false` is load-bearing, not a default worth inheriting: a
				// header row recurs on every continuation page, so an anchor in one reserves its
				// stamp's height on all of them — which is why the save refuses it outright.
				return editor
					.chain()
					.focus()
					.insertTable({ rows: 1, cols: 2, withHeaderRow: false })
					.run();
			},
			removeSignature: (slot) => {
				if (editor === null) {
					return;
				}
				const transaction = removeSignatureSlot(editor.state, slot);
				if (transaction !== null) {
					editor.view.dispatch(transaction);
				}
			},
			locateSignature: (slot) => {
				if (editor === null) {
					return;
				}
				const found = findSignatureForSlot(editor.state.doc, slot);
				if (found === null) {
					return;
				}
				// `setNodeSelection` + `scrollIntoView`: selecting the atom is what draws the
				// node view's selection ring, so "Locate" both scrolls to the anchor and says
				// which one it found.
				editor.chain().setNodeSelection(found.pos).scrollIntoView().run();
			},
			placedSignatureKeys: () =>
				editor === null ? new Set<string>() : placedSignatureKeys(editor.state.doc),
			insertDate: (templateDatePlacementId) => {
				if (editor === null) {
					return false;
				}
				const transaction = insertDateAt(
					editor.state,
					editor.state.selection.from,
					templateDatePlacementId
				);
				if (transaction === null) {
					return false;
				}
				editor.view.dispatch(transaction);
				editor.commands.focus();
				return true;
			},
			removeDate: (templateDatePlacementId) => {
				if (editor === null) {
					return;
				}
				const transaction = removeDateAnchor(editor.state, templateDatePlacementId);
				if (transaction !== null) {
					editor.view.dispatch(transaction);
				}
			},
			locateDate: (templateDatePlacementId) => {
				if (editor === null) {
					return;
				}
				const found = findDates(editor.state.doc).find(
					(entry) => entry.templateDatePlacementId === templateDatePlacementId
				);
				if (found === undefined) {
					return;
				}
				editor.chain().setNodeSelection(found.pos).scrollIntoView().run();
			},
			placedDateIds: () => (editor === null ? [] : placedDateIds(editor.state.doc)),
		}));

		return (
			<EditorStack>
				{editable && (
					<EditorToolbar
						editor={editor}
						baseFontSizePt={flowDocument.attrs.baseFontSizePt}
						baseFontFamily={flowDocument.attrs.baseFontFamily}
						{...(ruleActions === undefined ? {} : { ruleActions })}
					/>
				)}
				<EditorScroll data-testid="flow-document-scroll">
					<EditorSurface
						$pageWidthMm={flowDocument.attrs.page.widthMm}
						$marginMm={flowDocument.attrs.page.marginLeftMm}
						$baseFontFamily={fontStackFor(flowDocument.attrs.baseFontFamily)}
						$baseFontSizePt={flowDocument.attrs.baseFontSizePt}
					>
						<EditorContent editor={editor} />
					</EditorSurface>
				</EditorScroll>
			</EditorStack>
		);
	}
);

FlowDocumentEditor.displayName = 'FlowDocumentEditor';
